import { useEditorStore } from '@/stores/editor.ts'
import { useCreateTable } from './useCreateTable.ts'
import { useMergeTable } from './useMergeTable.ts'
import { useSelectorOverlay } from '@/views/editor/composable/table/selector/selctorOverlay.ts'
import { storeToRefs } from 'pinia'
import { activeFunction, calcFunction, cellMultiPosition, cellPosition, functionActivePosition, overFunctionList, clampPositionString } from '@/views/editor/composable/table/useFunction.ts'
import { useDeleteTable } from '@/views/editor/composable/table/useDeleteTable.ts'
import { UseSelection } from '@/views/editor/composable/useSelection.ts'
import { useCommonStore } from '@/stores/common.ts'
import { checkBlock, directSocketSave, socketSave, deletePrefix, setDataType } from '@/views/editor/composable/useNote.ts'
import { nextTick } from 'vue'
import { UseHistory } from '@/views/editor/composable/useHistory.ts'
import { TypingHistoryManager } from '@/views/editor/composable/useTypingHistory.ts'
import { escapeHtml } from '@/composables/common.ts'

// Global typing history manager for cleanup during Undo/Redo
let globalTypingHistoryManager: TypingHistoryManager | null = null

export function getGlobalTypingHistoryManager() {
	return globalTypingHistoryManager
}

export function useTable() {
	let tableSelector: HTMLDivElement | any
	let tableRow: number = 0
	let tableCol: number = 0

	let createTable: any
	let selectorOverlay: any
	let dragTableList: any = []
	let typingHistoryManager: TypingHistoryManager | null = null
	let previousCellTarget: HTMLTableCellElement | null = null
	let wasFunctionActiveOnEnter = false
	let selectorDragAnchor: HTMLElement | null = null

	function findSelectionBoundaryCell(corner: 'top-left' | 'bottom-right'): HTMLElement | null {
		const { cellTarget, dragList, tableSelector: tableSelectorStore } = storeToRefs(useEditorStore())
		const grid = tableSelectorStore.value

		if (!grid || grid.length === 0 || dragList.value.length < 2) {
			return cellTarget.value
		}

		const positions: { row: number; col: number }[] = []
		const processedCells = new Set()

		dragList.value.forEach((item: any) => {
			if (processedCells.has(item)) return
			processedCells.add(item)

			for (let r = 0; r < grid.length; r++) {
				for (let c = 0; c < grid[r].length; c++) {
					if (grid[r][c]?.html === item) {
						positions.push({ row: r, col: c })
					}
				}
			}
		})

		if (positions.length === 0) return cellTarget.value

		const minRow = Math.min(...positions.map(p => p.row))
		const maxRow = Math.max(...positions.map(p => p.row))
		const minCol = Math.min(...positions.map(p => p.col))
		const maxCol = Math.max(...positions.map(p => p.col))

		if (corner === 'top-left') {
			return grid[minRow]?.[minCol]?.html || cellTarget.value
		} else {
			return grid[maxRow]?.[maxCol]?.html || cellTarget.value
		}
	}

	function isCellInDragSelection(cell: HTMLElement, dragListValue: any[], tableSelectorRef: any): boolean {
		if (dragListValue.length < 2) return false

		const { tableSelector: tableSelectorStore } = storeToRefs(useEditorStore())
		const grid = tableSelectorStore.value
		if (!grid || grid.length === 0) return false

		function findCellPosition(targetCell: HTMLElement): { row: number; col: number } | null {
			for (let r = 0; r < grid.length; r++) {
				for (let c = 0; c < grid[r].length; c++) {
					if (grid[r][c]?.html === targetCell) {
						return { row: r, col: c }
					}
				}
			}
			return null
		}

		const positions: { row: number; col: number }[] = []
		dragListValue.forEach((item: any) => {
			const pos = findCellPosition(item)
			if (pos) positions.push(pos)
		})

		if (positions.length < 2) return false

		const minRow = Math.min(...positions.map(p => p.row))
		const maxRow = Math.max(...positions.map(p => p.row))
		const minCol = Math.min(...positions.map(p => p.col))
		const maxCol = Math.max(...positions.map(p => p.col))

		const cellPos = findCellPosition(cell)
		if (!cellPos) return false

		return cellPos.row >= minRow && cellPos.row <= maxRow && cellPos.col >= minCol && cellPos.col <= maxCol
	}

	function validateFunctionWithoutParens(cell: HTMLElement | null): boolean {
		if (!cell) return false

		const div = cell.querySelector('div[contenteditable]') as HTMLElement
		if (!div) return false

		// 함수 리스트가 표시 중이면 검증하지 않음 (입력 중)
		const functionListContainer = document.querySelector('.table--function__list')
		if (functionListContainer) return false

		const content = div.textContent || ''
		const { functionList } = storeToRefs(useEditorStore())

		if (content.startsWith('=') && !content.includes('(')) {
			const functionName = content.slice(1).trim().toUpperCase()

			if (functionName.length > 0) {
				if (functionList.value.includes(functionName)) {
					useDeleteTable().functionList(true)
					cell.dataset.type = 'error'
					div.innerHTML = `<span>#CALC!</span><i class="ico ico--table__close" data-testid="table-close-btn"></i>`
					div.setAttribute('contenteditable', 'false')
					return true
				} else {
					useDeleteTable().functionList(true)
					cell.dataset.type = 'error'
					div.innerHTML = `<span>#NAME!</span><i class="ico ico--table__close" data-testid="table-close-btn"></i>`
					div.setAttribute('contenteditable', 'false')
					return true
				}
			}
		}
		return false
	}

	function validateInvalidFunctionWithParens(cell: HTMLElement | null): boolean {
		if (!cell) return false

		const div = cell.querySelector('div[contenteditable]') as HTMLElement
		if (!div) return false

		// 함수 리스트가 표시 중이면 검증하지 않음 (입력 중)
		const functionListContainer = document.querySelector('.table--function__list')
		if (functionListContainer) return false

		const content = div.textContent || ''
		const { functionList } = storeToRefs(useEditorStore())

		if (content.startsWith('=') && content.includes('(')) {
			const match = content.match(/^=([A-Z]+)\(/i)
			if (match) {
				const functionName = match[1].toUpperCase()

				if (!functionList.value.includes(functionName)) {
					useDeleteTable().functionList(true)
					cell.dataset.type = 'error'
					div.innerHTML = `<span>#NAME!</span><i class="ico ico--table__close" data-testid="table-close-btn"></i>`
					div.setAttribute('contenteditable', 'false')
					return true
				}
			}
		}
		return false
	}

	async function create(row: number = 0, col: number = 0) {
		const { socket } = storeToRefs(useCommonStore())
		const { noteUid, selector, noteType } = storeToRefs(useEditorStore())

		if (!noteType.value || !noteUid.value || !selector.value || row === 0 || col === 0) return

		tableRow = 0
		tableCol = 0

		if (row !== 0) {
			tableRow = row
		}

		if (col !== 0) {
			tableCol = col
		}

		const rows: any = []
		const newBlock = !checkBlock()

		for (let i = 0; i < tableRow; i++) {
			rows[i] = []
			for (let j = 0; j < tableCol; j++) {
				rows[i].push({
					cellUid: `temp-${useCommonStore().uniqueId()}`,
					colSpan: 1,
					rowSpan: 1,
					content: '',
					dataType: {},
				})
			}
		}
		const tableParams = {
			noteUID: noteUid.value,
			blockUID: selector.value.id.includes('temp') ? '' : selector.value.id.replace('prefix-', ''),
			new: newBlock,
			content: {
				type: 'defaultTable',
				tableUID: `temp-${useCommonStore().uniqueId()}`,
				cell: rows,
			},
		}
		const { tableBlockTemp } = storeToRefs(useEditorStore())
		tableBlockTemp.value = tableParams
		const savedBlockUID = tableParams.blockUID
		await useCreateTable().table()

		if (noteType.value === 'Project') {
			const projectTableParams = {
				noteUID: noteUid.value,
				blockUID: savedBlockUID,
				new: newBlock,
				content: {
					type: 'defaultTable',
					tableUID: `temp-${useCommonStore().uniqueId()}`,
					cell: rows,
				},
			}
			socket.value.createTableBlock(projectTableParams)
		}
	}

	function keydownEvent(event: any) {
		const { isFunctionActive, functionTarget, functionList, isUndoRedoInProgress } = storeToRefs(useEditorStore())
		const { target } = event

		// Ctrl+A (Cmd+A on Mac) 전체 선택 처리
		const isSelectAll = (event.ctrlKey || event.metaKey) && event.key.toLowerCase() === 'a'
		if (isSelectAll) {
			const selection = window.getSelection()
			if (selection) {
				const range = document.createRange()
				range.selectNodeContents(target)
				selection.removeAllRanges()
				selection.addRange(range)
			}
			event.preventDefault()
			return
		}

		if (event.key === ':') {
			if (isFunctionActive.value) {
				const functionFilterList = ['SUM', 'AVG', 'AVERAGE', 'STDEV']
				if (!functionFilterList.includes(functionTarget.value.dataset.function)) {
					event.preventDefault()
					return
				}
			}
		}

		if (event.key === 'Enter' && event.altKey) {
			if (event.isComposing || event.keyCode === 229) return
			event.preventDefault()
			insertLineBreakInCell()
			return
		}

		if (event.key === 'Enter' || (event.key === 'Escape' && isFunctionActive.value)) {
			if (event.isComposing || event.keyCode === 229) return
			event.preventDefault()

			if (event.key === 'Enter') {
				wasFunctionActiveOnEnter = isFunctionActive.value
			}

			const cell = target.closest('td')
			useDeleteTable().functionList(true)
			if (validateFunctionWithoutParens(cell)) {
				return
			}

			if (validateInvalidFunctionWithParens(cell)) {
				if (!isUndoRedoInProgress.value) {
					const history = new UseHistory()
					history.push()
				}
				return
			}

			if (isFunctionActive.value) {
				finishFunction()
			}

			if (event.key === 'Enter' && !event.shiftKey && cell) {
				if (wasFunctionActiveOnEnter) {
					const table = cell.closest('table')
					const cellDiv = cell.querySelector('div')
					if (table && cellDiv) {
						createTable = useCreateTable()
						createTable.tableShapes(table)
						createTable.selector()
						selectorOverlay = useSelectorOverlay()
						selectorOverlay.positionSelector(cellDiv)
					}
				} else {
					moveToCellBelow(cell)
				}
			}
		} else if (event.key === 'ArrowUp' || event.key === 'ArrowDown' || event.key === 'ArrowLeft' || event.key === 'ArrowRight') {
			// IME 조합 중에는 셀 이동하지 않고 브라우저 기본 동작 허용
			if (event.isComposing) {
				return
			}

			// 캐럿 위치 확인하여 텍스트 편집 중에는 화살표 키 허용
			const selection = window.getSelection()
			if (selection && selection.rangeCount > 0) {
				const range = selection.getRangeAt(0)
				const textContent = target.textContent || ''

				// contenteditable div 내에서 실제 캐럿 위치 계산
				const preCaretRange = range.cloneRange()
				preCaretRange.selectNodeContents(target)
				preCaretRange.setEnd(range.endContainer, range.endOffset)
				const caretOffset = preCaretRange.toString().length

				// ArrowLeft: 캐럿이 텍스트 시작에 있지 않으면 허용
				if (event.key === 'ArrowLeft' && caretOffset > 0) {
					return
				}

				// ArrowLeft: 텍스트 시작에서 왼쪽 키 → 이전 셀로 이동
				if (event.key === 'ArrowLeft' && caretOffset === 0) {
					event.preventDefault()
					tableKeyMoveEvent(event)
					return
				}

				// ArrowRight: 캐럿이 텍스트 끝에 있지 않으면 허용
				if (event.key === 'ArrowRight' && caretOffset < textContent.length) {
					return
				}

				// ArrowRight: 텍스트 끝에서 오른쪽 키 → 다음 셀로 이동
				if (event.key === 'ArrowRight' && caretOffset >= textContent.length) {
					event.preventDefault()
					tableKeyMoveEvent(event)
					return
				}

				// ArrowUp/Down: 셀 이동
				if (event.key === 'ArrowUp' || event.key === 'ArrowDown') {
					event.preventDefault()
					tableKeyMoveEvent(event)
					return
				}
			}

			// 여기까지 오면 안됨 (모든 케이스 처리됨)
			event.preventDefault()
			return
		} else if (event.key === 'Tab') {
			if (event.isComposing || event.keyCode === 229) return
			event.preventDefault()
			const direction = event.shiftKey ? 'ArrowLeft' : 'ArrowRight'
			tableKeyMoveEvent({ target: event.target, key: direction, preventDefault: () => {} })
			return
		}
	}

	let isKeyChange = false

	function focusAndMoveCaretToEnd(div: HTMLElement) {
		div.focus()
		const range = document.createRange()
		const selection = window.getSelection()
		range.selectNodeContents(div)
		range.collapse(false)
		if (selection) {
			selection.removeAllRanges()
			selection.addRange(range)
		}
	}

	function findCellOriginInGrid(targetCell: HTMLElement): { row: number; col: number } | null {
		const { tableSelector } = storeToRefs(useEditorStore())
		const grid = tableSelector.value
		if (!grid) return null
		for (let r = 0; r < grid.length; r++) {
			for (let c = 0; c < grid[r].length; c++) {
				if (grid[r][c]?.html === targetCell) {
					return { row: r, col: c }
				}
			}
		}
		return null
	}

	function insertLineBreakInCell() {
		const selection = window.getSelection()
		if (!selection || selection.rangeCount === 0) return

		const range = selection.getRangeAt(0)
		range.deleteContents()

		const br = document.createElement('br')
		range.insertNode(br)

		const needsTrailingBr = !br.nextSibling || (br.nextSibling.nodeType === Node.TEXT_NODE && !br.nextSibling.textContent)
		if (needsTrailingBr) {
			const trailingBr = document.createElement('br')
			br.parentNode?.insertBefore(trailingBr, br.nextSibling)
		}

		range.setStartAfter(br)
		range.setEndAfter(br)
		selection.removeAllRanges()
		selection.addRange(range)
	}

	function moveToCellBelow(currentCell: HTMLTableCellElement): boolean {
		const { cellTarget, tableSelector, dragList } = storeToRefs(useEditorStore())
		const grid = tableSelector.value
		if (!grid || grid.length === 0) return false

		const origin = findCellOriginInGrid(currentCell)
		if (!origin) return false

		const rowSpan = currentCell.rowSpan || 1
		const targetRow = origin.row + rowSpan

		if (targetRow >= grid.length) return false

		const targetSlot = grid[targetRow][origin.col]
		if (!targetSlot?.html) return false

		const targetDiv = targetSlot.html.querySelector('div')
		if (!targetDiv) return false

		previousCellTarget = currentCell
		dragList.value = []
		focusAndMoveCaretToEnd(targetDiv)
		cellTarget.value = targetSlot.html

		return true
	}

	function tableKeyMoveEvent(event: any) {
		event.preventDefault()

		useDeleteTable().functionList(true)
		const { isFunctionActive, functionTarget } = storeToRefs(useEditorStore())
		isFunctionActive.value = false
		functionTarget.value = null

		if (!isKeyChange) {
			const { cellTarget, tableSelector } = storeToRefs(useEditorStore())
			isKeyChange = true
			const { target } = event
			const cell = target.closest('td') as HTMLTableCellElement

			const grid = tableSelector.value
			if (!cell || !grid || grid.length === 0) {
				queueMicrotask(() => {
					isKeyChange = false
				})
				return
			}

			const origin = findCellOriginInGrid(cell)
			if (!origin) {
				queueMicrotask(() => {
					isKeyChange = false
				})
				return
			}

			const rowSpan = cell.rowSpan || 1
			const colSpan = cell.colSpan || 1
			const totalRows = grid.length
			const totalCols = grid[0].length
			let targetRow = -1
			let targetCol = -1

			if (event.key === 'ArrowUp') {
				if (origin.row === 0) {
					queueMicrotask(() => {
						isKeyChange = false
					})
					return
				}
				targetRow = origin.row - 1
				targetCol = origin.col
			} else if (event.key === 'ArrowDown') {
				targetRow = origin.row + rowSpan
				if (targetRow >= totalRows) {
					queueMicrotask(() => {
						isKeyChange = false
					})
					return
				}
				targetCol = origin.col
			} else if (event.key === 'ArrowLeft') {
				if (origin.col === 0 && origin.row === 0) {
					queueMicrotask(() => {
						isKeyChange = false
					})
					return
				}
				if (origin.col === 0) {
					targetRow = origin.row - 1
					targetCol = grid[targetRow].length - 1
				} else {
					targetRow = origin.row
					targetCol = origin.col - 1
				}
			} else if (event.key === 'ArrowRight') {
				targetCol = origin.col + colSpan
				if (targetCol >= totalCols) {
					targetRow = origin.row + rowSpan
					if (targetRow >= totalRows) {
						queueMicrotask(() => {
							isKeyChange = false
						})
						return
					}
					targetCol = 0
				} else {
					targetRow = origin.row
				}
			} else {
				queueMicrotask(() => {
					isKeyChange = false
				})
				return
			}

			const targetSlot = grid[targetRow]?.[targetCol]
			if (!targetSlot?.html) {
				queueMicrotask(() => {
					isKeyChange = false
				})
				return
			}

			const targetDiv = targetSlot.html.querySelector('div')
			if (targetDiv) {
				focusAndMoveCaretToEnd(targetDiv)
				cellTarget.value = targetSlot.html
			}

			queueMicrotask(() => {
				isKeyChange = false
			})
		}
	}

	interface PasteCellInfo {
		content: string
		rowSpan: number
		colSpan: number
		isMergedSlot: boolean
	}

	function parseTableFromClipboard(clipboardData: DataTransfer): PasteCellInfo[][] | null {
		const htmlData = clipboardData?.getData('text/html') || ''
		const textData = clipboardData?.getData('text/plain') || ''

		if (htmlData) {
			const parser = new DOMParser()
			const doc = parser.parseFromString(htmlData, 'text/html')
			const table = doc.querySelector('table')
			if (table) {
				const grid = buildContentGridFromTable(table)
				if (grid && (grid.length > 1 || (grid.length === 1 && grid[0].length > 1))) {
					return grid
				}
			}
		}

		if (textData && textData.includes('\t')) {
			const lines = textData.split(/\r?\n/).filter(line => line.trim().length > 0)
			if (lines.length > 0) {
				const rows = lines.map(line => line.split('\t'))
				if (rows.length > 1 || (rows.length === 1 && rows[0].length > 1)) {
					return rows.map(row => row.map(cell => ({
						content: cell,
						rowSpan: 1,
						colSpan: 1,
						isMergedSlot: false,
					})))
				}
			}
		}

		return null
	}

	function buildContentGridFromTable(table: Element): PasteCellInfo[][] | null {
		const allRows = [...table.querySelectorAll('tr')]

		const dataRows = allRows.filter(row => {
			const cells = [...row.querySelectorAll('td, th')]
			return cells.some(cell => !cell.classList.contains('number--line'))
		})

		if (dataRows.length === 0) return null

		let maxCols = 0
		dataRows.forEach(row => {
			const cells = [...row.querySelectorAll('td, th')].filter(c => !c.classList.contains('number--line'))
			let count = 0
			cells.forEach(cell => { count += (cell as HTMLTableCellElement).colSpan || 1 })
			maxCols = Math.max(maxCols, count)
		})

		if (maxCols === 0) return null

		const grid: (PasteCellInfo | null)[][] = Array.from({ length: dataRows.length }, () => Array(maxCols).fill(null))

		dataRows.forEach((row, rowIdx) => {
			const cells = [...row.querySelectorAll('td, th')].filter(c => !c.classList.contains('number--line'))
			let colIdx = 0

			cells.forEach(cell => {
				const td = cell as HTMLTableCellElement
				while (colIdx < maxCols && grid[rowIdx][colIdx] !== null) colIdx++
				if (colIdx >= maxCols) return

				const div = td.querySelector('div[contenteditable]') || td.querySelector('div')
				const content = (div || td).textContent || ''

				const rowSpan = td.rowSpan || 1
				const colSpan = td.colSpan || 1

				for (let r = 0; r < rowSpan && (rowIdx + r) < dataRows.length; r++) {
					for (let c = 0; c < colSpan && (colIdx + c) < maxCols; c++) {
						if (r === 0 && c === 0) {
							grid[rowIdx + r][colIdx + c] = { content, rowSpan, colSpan, isMergedSlot: false }
						} else {
							grid[rowIdx + r][colIdx + c] = { content: '', rowSpan: 1, colSpan: 1, isMergedSlot: true }
						}
					}
				}

				colIdx += colSpan
			})
		})

		return grid.map(row => row.map(cell => cell ?? { content: '', rowSpan: 1, colSpan: 1, isMergedSlot: false }))
	}

	function findMergedCellsInRange(grid: any[], startRow: number, startCol: number, rowCount: number, colCount: number): { cell: HTMLTableCellElement; anchorRow: number; anchorCol: number }[] {
		const endRow = Math.min(startRow + rowCount - 1, grid.length - 1)
		const endCol = Math.min(startCol + colCount - 1, (grid[0]?.length || 0) - 1)
		const found: { cell: HTMLTableCellElement; anchorRow: number; anchorCol: number }[] = []
		const seen = new Set<Element>()

		for (let r = startRow; r <= endRow; r++) {
			for (let c = startCol; c <= endCol; c++) {
				const cell = grid[r]?.[c]?.html as HTMLTableCellElement | null
				if (!cell || cell.classList?.contains('number--line') || seen.has(cell)) continue
				seen.add(cell)

				if (cell.rowSpan > 1 || cell.colSpan > 1) {
					let anchorRow = r
					let anchorCol = c
					for (let ar = 0; ar <= r; ar++) {
						for (let ac = 0; ac < (grid[ar]?.length || 0); ac++) {
							if (grid[ar][ac]?.html === cell) {
								anchorRow = ar
								anchorCol = ac
								break
							}
						}
						if (anchorRow < r) break
					}
					found.push({ cell, anchorRow, anchorCol })
				}
			}
		}
		return found
	}

	function checkMergeConflict(grid: any[], startRow: number, startCol: number, rowCount: number, colCount: number): boolean {
		const endRow = Math.min(startRow + rowCount - 1, grid.length - 1)
		const endCol = Math.min(startCol + colCount - 1, (grid[0]?.length || 0) - 1)
		const seen = new Set<Element>()

		for (let r = startRow; r <= endRow; r++) {
			for (let c = startCol; c <= endCol; c++) {
				const cell = grid[r]?.[c]?.html as HTMLTableCellElement | null
				if (!cell || cell.classList?.contains('number--line') || seen.has(cell)) continue
				seen.add(cell)

				if (cell.rowSpan <= 1 && cell.colSpan <= 1) continue

				let anchorRow = -1
				let anchorCol = -1
				outer: for (let ar = 0; ar < grid.length; ar++) {
					for (let ac = 0; ac < (grid[ar]?.length || 0); ac++) {
						if (grid[ar][ac]?.html === cell) {
							anchorRow = ar
							anchorCol = ac
							break outer
						}
					}
				}

				const cellEndRow = anchorRow + cell.rowSpan - 1
				const cellEndCol = anchorCol + cell.colSpan - 1

				if (anchorRow < startRow || anchorCol < startCol || cellEndRow > endRow || cellEndCol > endCol) {
					return true
				}
			}
		}
		return false
	}

	async function pasteTableDataToCells(pasteGrid: PasteCellInfo[][]): Promise<boolean> {
		const { cellTarget, tableSelector: tableSelectorRef, noteType, isUndoRedoInProgress } = storeToRefs(useEditorStore())
		const grid = tableSelectorRef.value

		if (!grid || grid.length === 0 || !cellTarget.value) return false

		let startRow = -1
		let startCol = -1
		for (let r = 0; r < grid.length && startRow === -1; r++) {
			for (let c = 0; c < grid[r].length; c++) {
				if (grid[r][c]?.html === cellTarget.value) {
					startRow = r
					startCol = c
					break
				}
			}
		}

		if (startRow === -1 || startCol === -1) return false

		const pasteRowCount = pasteGrid.length
		const pasteColCount = Math.max(...pasteGrid.map(row => row.length))

		if (checkMergeConflict(grid, startRow, startCol, pasteRowCount, pasteColCount)) {
			useCommonStore().open('error', '붙여넣기 오류', '병합된 셀에 붙여넣을 수 없습니다.', null)
			return false
		}

		const mergedCells = findMergedCellsInRange(grid, startRow, startCol, pasteRowCount, pasteColCount)
		const table = cellTarget.value.closest('table') as HTMLTableElement

		if (mergedCells.length > 0 && table) {
			const { unmergeTarget } = useMergeTable()
			for (const { cell } of mergedCells) {
				await unmergeTarget(cell)
			}
		}

		const freshGrid = tableSelectorRef.value
		if (!freshGrid || freshGrid.length === 0) return false

		const hasMergeInSource = pasteGrid.some(row => row.some(c => c.rowSpan > 1 || c.colSpan > 1))

		const processedCells = new Set<Element>()

		for (let r = 0; r < pasteGrid.length; r++) {
			for (let c = 0; c < pasteGrid[r].length; c++) {
				const cellInfo = pasteGrid[r][c]
				if (cellInfo.isMergedSlot) continue

				const targetRow = startRow + r
				const targetCol = startCol + c

				if (targetRow >= freshGrid.length) continue
				if (targetCol >= (freshGrid[targetRow]?.length || 0)) continue

				const targetSlot = freshGrid[targetRow][targetCol]
				if (!targetSlot?.html) continue

				const targetCell = targetSlot.html as HTMLTableCellElement
				if (targetCell.classList?.contains('number--line')) continue
				if (processedCells.has(targetCell)) continue
				processedCells.add(targetCell)

				const div = targetCell.querySelector('div') as HTMLElement
				if (!div) continue

				delete targetCell.dataset.type
				delete targetCell.dataset.function
				delete targetCell.dataset.formulas
				delete targetCell.dataset.position

				div.textContent = cellInfo.content
				div.setAttribute('contenteditable', 'true')

				if (noteType.value === 'Project') {
					directSocketSave('cell', targetCell)
				}
			}
		}

		if (hasMergeInSource && table) {
			await applySourceMerges(pasteGrid, freshGrid, startRow, startCol, table)
		} else if (processedCells.size > 0) {
			if (!isUndoRedoInProgress.value) {
				const history = new UseHistory()
				history.push()
			}
		}

		return true
	}

	async function applySourceMerges(pasteGrid: PasteCellInfo[][], grid: any[], startRow: number, startCol: number, table: HTMLTableElement) {
		const { dragList, cellTarget, tableSelector: tableSelectorRef, isUndoRedoInProgress } = storeToRefs(useEditorStore())

		for (let r = 0; r < pasteGrid.length; r++) {
			for (let c = 0; c < pasteGrid[r].length; c++) {
				const cellInfo = pasteGrid[r][c]
				if (cellInfo.isMergedSlot || (cellInfo.rowSpan <= 1 && cellInfo.colSpan <= 1)) continue

				const targetRow = startRow + r
				const targetCol = startCol + c

				if (targetRow >= grid.length) continue
				if (targetCol >= (grid[targetRow]?.length || 0)) continue

				const anchorSlot = grid[targetRow][targetCol]
				if (!anchorSlot?.html) continue

				const anchorCell = anchorSlot.html as HTMLTableCellElement
				if (anchorCell.classList?.contains('number--line')) continue

				const mergeEndRow = Math.min(targetRow + cellInfo.rowSpan - 1, grid.length - 1)
				const mergeEndCol = Math.min(targetCol + cellInfo.colSpan - 1, (grid[targetRow]?.length || 0) - 1)

				const cellsToSelect: HTMLTableCellElement[] = []
				for (let mr = targetRow; mr <= mergeEndRow; mr++) {
					for (let mc = targetCol; mc <= mergeEndCol; mc++) {
						const slot = grid[mr]?.[mc]
						if (slot?.html && !cellsToSelect.includes(slot.html)) {
							cellsToSelect.push(slot.html)
						}
					}
				}

				if (cellsToSelect.length >= 2) {
					dragList.value = cellsToSelect
					cellTarget.value = anchorCell
					const { merge } = useMergeTable()
					await merge()
				}
			}
		}

		dragList.value = []
		cellTarget.value = null

		useCreateTable().tableShapes(table)

		if (!isUndoRedoInProgress.value) {
			const history = new UseHistory()
			history.push()
		}
	}

	function buildCopyDataFromSelection(dragListValue: any[], grid: any[]): { html: string; text: string } | null {
		const positions: { row: number; col: number }[] = []
		const visited = new Set()

		dragListValue.forEach((item: any) => {
			if (visited.has(item)) return
			visited.add(item)
			for (let r = 0; r < grid.length; r++) {
				for (let c = 0; c < grid[r].length; c++) {
					if (grid[r][c]?.html === item) {
						positions.push({ row: r, col: c })
					}
				}
			}
		})

		if (positions.length === 0) return null

		const minRow = Math.min(...positions.map(p => p.row))
		const maxRow = Math.max(...positions.map(p => p.row))
		const minCol = Math.min(...positions.map(p => p.col))
		const maxCol = Math.max(...positions.map(p => p.col))

		let html = '<table>'
		let text = ''
		const processedCells = new Set<Element>()

		for (let r = minRow; r <= maxRow; r++) {
			html += '<tr>'
			const rowTexts: string[] = []

			for (let c = minCol; c <= maxCol; c++) {
				const slot = grid[r]?.[c]
				const cell = slot?.html as HTMLTableCellElement | null

				if (!cell || cell.classList?.contains('number--line')) {
					html += '<td><div contenteditable="true"></div></td>'
					rowTexts.push('')
					continue
				}

				if (processedCells.has(cell)) continue
				processedCells.add(cell)

				let cellText = ''
				if (cell.dataset?.type === 'function') {
					cellText = cell.querySelector('span')?.textContent || ''
				} else {
					const div = cell.querySelector('div') as HTMLElement
					cellText = div?.textContent || cell.textContent || ''
				}

				const rs = cell.rowSpan || 1
				const cs = cell.colSpan || 1
				let attrs = ''
				if (rs > 1) attrs += ` rowspan="${rs}"`
				if (cs > 1) attrs += ` colspan="${cs}"`

				html += `<td${attrs}><div contenteditable="true">${escapeHtml(cellText)}</div></td>`
				rowTexts.push(cellText)

				for (let fc = 1; fc < cs; fc++) {
					rowTexts.push('')
				}
			}

			html += '</tr>'
			text += rowTexts.join('\t') + '\n'
		}

		html += '</table>'
		text = text.trimEnd()

		return { html, text }
	}

	function clearSelectedCells(dragListValue: any[], grid: any[]) {
		const positions: { row: number; col: number }[] = []
		const visited = new Set()

		dragListValue.forEach((item: any) => {
			if (visited.has(item)) return
			visited.add(item)
			for (let r = 0; r < grid.length; r++) {
				for (let c = 0; c < grid[r].length; c++) {
					if (grid[r][c]?.html === item) {
						positions.push({ row: r, col: c })
					}
				}
			}
		})

		if (positions.length === 0) return

		const minRow = Math.min(...positions.map(p => p.row))
		const maxRow = Math.max(...positions.map(p => p.row))
		const minCol = Math.min(...positions.map(p => p.col))
		const maxCol = Math.max(...positions.map(p => p.col))

		const { noteType, isUndoRedoInProgress } = storeToRefs(useEditorStore())
		const processedCells = new Set<Element>()

		for (let r = minRow; r <= maxRow; r++) {
			for (let c = minCol; c <= maxCol; c++) {
				const slot = grid[r]?.[c]
				if (!slot?.html) continue

				const cell = slot.html as HTMLTableCellElement
				if (cell.classList?.contains('number--line')) continue
				if (processedCells.has(cell)) continue
				processedCells.add(cell)

				const div = cell.querySelector('div') as HTMLElement
				if (!div) continue

				delete cell.dataset.type
				delete cell.dataset.function
				delete cell.dataset.formulas
				delete cell.dataset.position

				div.textContent = ''
				div.setAttribute('contenteditable', 'true')

				if (noteType.value === 'Project') {
					directSocketSave('cell', cell)
				}
			}
		}

		if (processedCells.size > 0 && !isUndoRedoInProgress.value) {
			const history = new UseHistory()
			history.push()
		}
	}

	async function pasteEvent(event: any) {
		const { isFunctionActive, functionTarget, functionList, savedRange } = storeToRefs(useEditorStore())

		if (isFunctionActive.value) {
			event.preventDefault()
			const clipboardData = event.clipboardData
			const pastedText = clipboardData?.getData('text/plain') || ''
			if (!pastedText) return

			const selection = window.getSelection()
			if (selection && selection.rangeCount > 0) {
				const range = selection.getRangeAt(0)
				range.deleteContents()
				const textNode = document.createTextNode(pastedText)
				range.insertNode(textNode)
				range.setStartAfter(textNode)
				range.collapse(true)
				selection.removeAllRanges()
				selection.addRange(range)
			}

			const { target } = event
			const content = target.textContent || ''

			if (content.startsWith('=') && content.includes('(')) {
				let testText = content.slice(1)
				testText = testText.slice(0, testText.indexOf('('))
				const testTextUpper = testText.toUpperCase()

				if (functionList.value.includes(testTextUpper)) {
					functionTarget.value = target.closest('td')
					target.closest('td').dataset.type = 'function'
					target.closest('td').dataset.function = testTextUpper
					target.closest('td').dataset.formulas = 'true'

					let contentTotalLength = content.length
					if (content.includes(')')) {
						contentTotalLength--
					}
					const functionContent = content.slice(content.indexOf('(') + 1, contentTotalLength)
					functionActivePosition(functionContent)
				} else {
					finishFunction()
				}
			} else {
				const cell = target.closest('td')
				if (cell) {
					isFunctionActive.value = false
					delete cell.dataset.type
					delete cell.dataset.function
					delete cell.dataset.formulas
					delete cell.dataset.position
					functionTarget.value = null
				}
			}
			return
		}

		const { target } = event
		const td = target.closest('td')
		if (!td || td.classList.contains('number--line')) return

		const clipboardData = event.clipboardData

		const pasteGrid = parseTableFromClipboard(clipboardData)
		if (pasteGrid) {
			event.preventDefault()
			await pasteTableDataToCells(pasteGrid)
			return
		}

		const pastedText = clipboardData?.getData('text/plain') || ''

		// Check if pasted content is a function formula (starts with =)
		if (pastedText.trim().startsWith('=')) {
			event.preventDefault()

			// Extract function name from formula (e.g., =SUM(...) => SUM)
			const match = pastedText.match(/^=([A-Z]+)\(/)
			if (match) {
				const functionName = match[1].toUpperCase()
				const { functionList } = storeToRefs(useEditorStore())

				// Check if it's a valid function
				if (functionList.value.includes(functionName)) {
					// Set cell as function type
					td.dataset.type = 'function'
					td.dataset.function = functionName
					td.dataset.formulas = 'false'

					// Extract position parameters from formula (content between parentheses)
					const positionMatch = pastedText.match(/\(([^)]*)\)/)
					if (positionMatch) {
						td.dataset.position = positionMatch[1]
					}

					// Update cell content
					const cellDiv = td.querySelector('div')
					if (cellDiv) {
						cellDiv.innerHTML = `<span>${escapeHtml(pastedText)}</span><i class="ico ico--table__close" data-testid="table-close-btn"></i>`
						cellDiv.setAttribute('contenteditable', 'false')
					}

					// Calculate the function result
					try {
						calcFunction()
					} catch (error) {
						console.error('[pasteEvent] calcFunction 실패:', error)
						if (cellDiv) {
							cellDiv.innerHTML = `<span>#ERROR!</span><i class="ico ico--table__close" data-testid="table-close-btn"></i>`
						}
					}

					// Save the cell
					const { noteType, cellTarget } = storeToRefs(useEditorStore())
					cellTarget.value = td
					if (noteType.value === 'Project') {
						directSocketSave('function', td)
					}

					// Save to history
					const { isUndoRedoInProgress } = storeToRefs(useEditorStore())
					if (!isUndoRedoInProgress.value) {
						const history = new UseHistory()
						history.push()
					}

					return
				}
			}
		}

		// XSS/인젝션 방어: 모든 붙여넣기 콘텐츠를 순수 텍스트로 처리
		// <script>, SQL 인젝션 패턴 등이 HTML로 해석되지 않도록 방지
		event.preventDefault()
		if (pastedText) {
			const selection = window.getSelection()
			if (selection && selection.rangeCount > 0) {
				const range = selection.getRangeAt(0)
				range.deleteContents()
				const textNode = document.createTextNode(pastedText)
				range.insertNode(textNode)
				range.setStartAfter(textNode)
				range.collapse(true)
				selection.removeAllRanges()
				selection.addRange(range)
			}
		}
		const syntheticInput = new Event('input', { bubbles: true })
		td.dispatchEvent(syntheticInput)
	}

	function copyEvent(event: any) {
		const { target } = event
		const td = target.closest('td')
		if (!td || td.classList.contains('number--line')) return

		const { dragList, tableSelector: tableSelectorRef } = storeToRefs(useEditorStore())

		if (dragList.value.length >= 2) {
			const grid = tableSelectorRef.value
			if (grid && grid.length > 0) {
				const result = buildCopyDataFromSelection(dragList.value, grid)
				if (result) {
					event.preventDefault()
					event.stopImmediatePropagation()
					if (event.clipboardData) {
						event.clipboardData.setData('text/html', result.html)
						event.clipboardData.setData('text/plain', result.text)
					}
					return
				}
			}
		}

		const selection = window.getSelection()
		if (!selection || selection.rangeCount === 0) return

		const range = selection.getRangeAt(0)
		if (range.collapsed) return

		event.preventDefault()

		let selectedText = ''

		if (td.dataset.type === 'function') {
			const span = td.querySelector('span')
			if (span) {
				selectedText = span.textContent || ''
			}
		} else {
			selectedText = range.toString()
		}

		if (event.clipboardData && selectedText) {
			event.clipboardData.setData('text/plain', selectedText)
		}
	}

	function cutEvent(event: any) {
		const { target } = event
		const td = target.closest('td')
		if (!td || td.classList.contains('number--line')) return

		const { dragList, tableSelector: tableSelectorRef } = storeToRefs(useEditorStore())

		if (dragList.value.length >= 2) {
			const grid = tableSelectorRef.value
			if (grid && grid.length > 0) {
				const result = buildCopyDataFromSelection(dragList.value, grid)
				if (result) {
					event.preventDefault()
					event.stopImmediatePropagation()
					if (event.clipboardData) {
						event.clipboardData.setData('text/html', result.html)
						event.clipboardData.setData('text/plain', result.text)
					}
					clearSelectedCells(dragList.value, grid)
					return
				}
			}
		}

		const selection = window.getSelection()
		if (!selection || selection.rangeCount === 0) return

		const range = selection.getRangeAt(0)
		if (range.collapsed) return

		event.preventDefault()

		let selectedText = ''

		if (td.dataset.type === 'function') {
			const span = td.querySelector('span')
			if (span) {
				selectedText = span.textContent || ''
			}
		} else {
			selectedText = range.toString()
		}

		if (event.clipboardData && selectedText) {
			event.clipboardData.setData('text/plain', selectedText)
		}

		// Cut: 선택된 내용 삭제
		range.deleteContents()

		// DOM 변경 후 input 이벤트 트리거하여 history 저장
		if (td) {
			const inputEvent = new Event('input', { bubbles: true })
			td.dispatchEvent(inputEvent)
		}
	}

	async function keyupEvent(event: any) {
		const { isFunctionActive, functionList, cellTarget, savedRange, functionTarget, noteType, changeRange, isUndoRedoInProgress } = storeToRefs(useEditorStore())
		const { target } = event

		// Undo/Redo 중에는 keyup 이벤트 무시
		if (isUndoRedoInProgress.value) {
			return
		}

		// IME 조합 중에는 keyup 이벤트 무시 (한글 등 조합형 문자 입력 시 조합이 깨지는 것 방지)
		if (event.isComposing) {
			return
		}

		const content = target.textContent
		const history = new UseHistory()

		// Backspace/Delete로 내용이 비었으면 function 상태 해제
		if (event.key === 'Backspace' || event.key === 'Delete') {
			const trimmedContent = content?.trim() || ''
			if (trimmedContent === '' || trimmedContent === '=') {
				const { dragList } = storeToRefs(useEditorStore())
				const cell = target.closest('td')
				const table = cell.closest('table')
				const cellDiv = table.querySelectorAll('div.active')

				if (isFunctionActive.value || cell?.dataset.type === 'function') {
					isFunctionActive.value = false
					dragList.value = []

					cellDiv.forEach((element: any) => {
						element.classList.remove('active')
					})

					// 셀 dataset 초기화
					if (cell) {
						delete cell.dataset.type
						delete cell.dataset.function
						delete cell.dataset.formulas
						delete cell.dataset.position

						// div contenteditable 복원 및 내용 초기화
						const div = cell.querySelector('div')
						if (div) {
							div.setAttribute('contenteditable', 'true')
							div.innerHTML = ''
						}
					}

					// history 저장
					if (!isUndoRedoInProgress.value) {
						history.push()
					}
				}
			}
		}

		if (event.key === '=' && content.charAt(0) === '=') {
			useCreateTable().addFunctionList()
			return
		} else {
			if (target.innerHTML.charAt(0) === '=') {
				functionTarget.value = target.closest('td')
				if (content.includes('(')) {
					useDeleteTable().functionList(true)

					let testText = content.slice(1)
					testText = testText.slice(0, testText.indexOf('('))
					const testTextUpper = testText.toUpperCase()

					if (functionList.value.includes(testTextUpper)) {
						isFunctionActive.value = true
						target.closest('td').dataset.type = 'function'
						target.closest('td').dataset.formulas = 'true'
						target.closest('td').dataset.function = testTextUpper

						if (testText !== testTextUpper) {
							// 현재 커서 위치 저장
							const selection = window.getSelection()
							let cursorPosition = content.length

							if (selection && selection.rangeCount > 0) {
								try {
									cursorPosition = selection.getRangeAt(0).startOffset
								} catch (e) {
									console.warn('커서 위치 저장 실패:', e)
								}
							}

							const afterParen = content.slice(content.indexOf('('))
							const newContent = '=' + testTextUpper + afterParen

							target.textContent = newContent

							// setTimeout 제거 - 동기적으로 커서 복원
							const range = document.createRange()
							const newSelection = window.getSelection()

							if (target.firstChild && newSelection) {
								try {
									const safePosition = Math.min(cursorPosition, newContent.length)
									range.setStart(target.firstChild, safePosition)
									range.collapse(true)
									newSelection.removeAllRanges()
									newSelection.addRange(range)
								} catch (e) {
									console.warn('커서 복원 실패:', e)
								}
							}
						}
					}
				}

				if (!isFunctionActive.value && !content.includes('(')) {
					useCreateTable().searchFunctionList(content.slice(1))
				} else {
					let testText = content.slice(1)
					testText = testText.slice(0, testText.indexOf('('))
					if (!functionList.value.includes(testText.toUpperCase())) {
						isFunctionActive.value = false
						target.closest('td').dataset.type = ''
					} else {
						useDeleteTable().functionList(true)
						let contentTotalLength = content.length
						if (content.includes(')')) {
							contentTotalLength--
						}
						const functionContent = content.slice(content.indexOf('(') + 1, contentTotalLength)
						functionActivePosition(functionContent)
					}

					const selection: any = window.getSelection()
					if (selection && selection.rangeCount > 0) {
						try {
							savedRange.value = selection.getRangeAt(0).cloneRange()
						} catch (e) {
							console.warn('Range 저장 실패:', e)
						}
					}
				}
			} else if (event.key === 'ArrowUp' || event.key === 'ArrowDown' || event.key === 'ArrowLeft' || event.key === 'ArrowRight') {
				// keydown에서 모든 화살표 키 처리를 완료하므로 keyup에서는 무시
				return
			} else if (event.key === 'Backspace' || event.key === 'Delete') {
				return
			} else {
				calcFunction()
			}
		}

		if (noteType.value === 'Project') {
			socketSave('cell', cellTarget.value)
		}

		// TypingHistoryManager 초기화 (한 번만)
		if (!typingHistoryManager) {
			typingHistoryManager = new TypingHistoryManager(history)
			globalTypingHistoryManager = typingHistoryManager
		}

		// Enter 키: 즉시 히스토리 저장
		if (event.key === 'Enter') {
			if (!event.shiftKey && !isUndoRedoInProgress.value && !wasFunctionActiveOnEnter) {
				await typingHistoryManager.onEnter()
			}
			wasFunctionActiveOnEnter = false
			return
		}

		// Backspace/Delete: 삭제 히스토리 관리
		if (event.key === 'Backspace' || event.key === 'Delete') {
			await typingHistoryManager.onDelete(event.key)
			return
		}

		// 일반 키 입력: 타이핑 모드로 전환
		const isControlKey = event.key.match(/^(Shift|Control|Alt|Meta|Arrow.*|Tab|Escape|F\d+)$/)
		if (!isControlKey) {
			await typingHistoryManager.onRegularKey()
		}
	}

	async function inputEvent(event: any) {
		const history = new UseHistory()

		// TypingHistoryManager 초기화 (한 번만)
		if (!typingHistoryManager) {
			typingHistoryManager = new TypingHistoryManager(history)
			globalTypingHistoryManager = typingHistoryManager
		}

		// input 이벤트: 스페이스, 구두점 자동 감지 및 1초 타이머
		await typingHistoryManager.onInput(event as InputEvent)
	}

	async function focusEvent(event: any) {
		const { target } = event
		const { selector, cellTarget, isContextMenu, isFunctionActive, isUndoRedoInProgress, functionList } = storeToRefs(useEditorStore())
		const editorEl = document.getElementById('editor')
		const leavingTable = editorEl?.dataset.leavingTable === 'true'

		if (leavingTable) {
			return
		}

		// Undo/Redo 중에는 focus 이벤트 무시
		if (isUndoRedoInProgress.value) {
			return
		}

		selector.value = target.closest('.editor--block')
		const newCell = target.closest('td')
		const table = target.closest('table')

		// 수식 활성화 상태에서는 selector를 이동하지 않음
		if (table && isFunctionActive.value) {
			return
		}

		// 추가 방어: 계산식 셀에서는 selector 삭제 차단
		if (newCell?.dataset?.type === 'function') {
			return
		}

		if (newCell && previousCellTarget && newCell !== previousCellTarget && isContextMenu.value === false && !isFunctionActive.value && previousCellTarget.dataset.type !== 'function') {
			if (typingHistoryManager) {
				typingHistoryManager.flush()
			}
			useDeleteTable().selector()
			previousCellTarget = null
		}

		if (table) {
			// cellTarget을 먼저 업데이트하여 functionList 위치가 올바르게 계산되도록 함
			if (newCell) {
				cellTarget.value = newCell
			}

			if (target.innerHTML.charAt(0) === '=' && !target.innerHTML.includes('(')) {
				const functionContainer = document.querySelector('.table--function__list')

				if (!functionContainer) {
					const useSelection = new UseSelection()
					useSelection.setChangeSelection()
					useCreateTable().addFunctionList()
				}
			}

			// 계산식 편집 모드나 계산식 셀에서는 selector 삭제하지 않음
			if (!isFunctionActive.value && newCell?.dataset?.type !== 'function') {
				useDeleteTable().selector()
			}

			useCreateTable().tableShapes(table)

			createTable = useCreateTable()
			createTable.widthSizeItem(table)
			createTable.heightSizeItem(table)

			createTable.selector()

			if (!isContextMenu.value) {
				selectorOverlay = useSelectorOverlay()
				selectorOverlay.positionSelector(target)
			}
		}
	}

	function tableMouseDownEvent(event: any) {
		const { isContextMenu, dragList, cellTarget, isFunctionActive, functionKeyType, functionStartTarget, functionTarget } = storeToRefs(useEditorStore())

		event.stopPropagation()

		// 이전에 포커스된 텍스트 블록이 있으면 blockEditEnd 호출
		// (preventDefault로 인해 focusout이 발생하지 않는 경우 대비)
		const { selector, noteUid } = storeToRefs(useEditorStore())
		const { socket } = storeToRefs(useCommonStore())
		if (selector.value?.dataset?.type === 'text' && socket.value) {
			socket.value.blockEditEnd(noteUid.value, deletePrefix(selector.value.id))
		}

		// 비편집 셀 클릭 시 브라우저 기본 선택 동작 차단
		const { target } = event
		const clickedCell = target.closest('td')

		if (clickedCell && !clickedCell.classList.contains('number--line') && !isFunctionActive.value) {
			const cellDiv = clickedCell.querySelector('div[contenteditable="false"]')
			if (cellDiv) {
				event.preventDefault()
			}
		}

		// 이전 셀이 함수명만 있는 경우 #CALC! 에러 표시
		const newCell = target.closest('td')
		if (newCell && cellTarget.value && newCell !== cellTarget.value) {
			useDeleteTable().functionList(true)
			validateFunctionWithoutParens(cellTarget.value)
			validateInvalidFunctionWithParens(cellTarget.value)
		}

		const blockList = document.querySelectorAll('.editor--block')

		blockList.forEach((block: any) => {
			block.style.zIndex = '1'
			block.classList.remove('active')
		})

		if (event.button !== 0) {
			event.preventDefault()
			const rightClickCell = target.closest('td')

			if (rightClickCell && !rightClickCell.classList.contains('number--line')) {
				const isInSelection = isCellInDragSelection(rightClickCell, dragList.value, tableSelector)

				if (isInSelection) {
					isContextMenu.value = true
				} else {
					isContextMenu.value = false
					dragList.value = []
					cellTarget.value = rightClickCell

					const { selector } = storeToRefs(useEditorStore())
					const editorBlock = rightClickCell.closest('.editor--block')

					useDeleteTable().selector()

					if (editorBlock) {
						selector.value = editorBlock
					}

					const table = rightClickCell.closest('table')
					if (table) {
						const createTableInstance = useCreateTable()
						createTableInstance.tableShapes(table)
						createTableInstance.selector()
						createTableInstance.widthSizeItem(table)
						createTableInstance.heightSizeItem(table)

						const cellDiv = rightClickCell.querySelector('div[contenteditable]')
						if (cellDiv) {
							useSelectorOverlay().positionSelector(cellDiv)
						}
					}
				}
			} else {
				isContextMenu.value = dragList.value.length > 1
			}
			return
		} else {
			isContextMenu.value = false
		}

		const parent = target.closest('td')
		if (target.classList.contains('ico') || !parent || parent.classList.contains('number--line')) {
			dragTableList = []
			dragList.value = []
			document.removeEventListener('mousemove', tableMouseMoveEvent)
			document.removeEventListener('mouseup', tableMouseUpEvent)
			return
		}

		if (isFunctionActive.value && parent) {
			const functionTable = functionTarget.value?.closest('table')
			const clickedTable = parent.closest('table')
			if (functionTable && clickedTable && functionTable !== clickedTable) {
				finishFunction()
			}
		}

		const parentFunctionType = parent.dataset.type
		let functionFilterActive = false

		if (isFunctionActive.value) {
			const functionFilterList = ['SUM', 'AVG', 'AVERAGE', 'STDEV']
			if (functionFilterList.includes(functionTarget.value.dataset.function)) {
				if (event.shiftKey) {
					functionKeyType.value = 'shift'
				} else if (event.ctrlKey) {
					functionKeyType.value = 'ctrl'
				} else {
					functionKeyType.value = ''
				}
			} else {
				functionFilterActive = true
			}
		}

		if (!isFunctionActive.value && parentFunctionType === 'function') {
			parent.dataset.formulas = 'true'
			functionFilterActive = true
		}

		if (parent && !isFunctionActive.value) {
			previousCellTarget = cellTarget.value
			cellTarget.value = parent
		}

		if (isFunctionActive.value) {
			functionStartTarget.value = parent
		}

		dragTableList = []
		dragList.value = []
		if (!functionFilterActive) {
			document.addEventListener('mousemove', tableMouseMoveEvent)
		}
		document.addEventListener('mouseup', tableMouseUpEvent)
	}

	function tableMouseMoveEvent(event: any) {
		const { cellTarget, isFunctionActive, functionStartTarget, functionTarget } = storeToRefs(useEditorStore())
		const { target } = event
		const parent = target.closest('td')
		const table = target.closest('table')
		dragTableList = []
		if (isFunctionActive.value) {
			const functionTable = functionTarget.value?.closest('table')
			if (functionTable && table && functionTable !== table) {
				return
			}
			if (target.closest('td') !== functionTarget.value) {
				dragTableList.push(functionStartTarget.value)

				if (parent && !dragTableList.includes(parent) && !parent.classList.contains('number--line')) {
					dragTableList.push(parent)
				}
			} else {
				return false
			}
		} else {
			dragTableList.push(cellTarget.value)

			if (parent && !dragTableList.includes(parent) && !parent.classList.contains('number--line')) {
				dragTableList.push(parent)
			}
		}

		const { dragList } = storeToRefs(useEditorStore())
		dragList.value = []
		dragList.value = dragTableList

		if (isFunctionActive.value) {
			const position: any = cellMultiPosition()
			functionActivePosition(position)
			return
		}

		selectorOverlay = useSelectorOverlay()
		selectorOverlay.positionMultiSelector(table, dragList.value)
	}

	function tableMouseUpEvent(event: any) {
		const { target } = event
		const { dragList, functionTarget, functionKeyType, isFunctionActive } = storeToRefs(useEditorStore())

		if (dragList.value.length < 2) {
			// 드래그하지 않고 function 셀을 클릭했을 때
			if (!isFunctionActive.value) {
				const clickedCell = target.closest('td')
				if (clickedCell && clickedCell.dataset.type === 'function' && !clickedCell.classList.contains('number--line')) {
					functionTarget.value = clickedCell
					activeFunction()
					document.removeEventListener('mousemove', tableMouseMoveEvent)
					document.removeEventListener('mouseup', tableMouseUpEvent)
					return
				}
			}

			selectorOverlay = useSelectorOverlay()

			const clickedCell = target.closest('td')
			if (clickedCell && !clickedCell.classList.contains('number--line') && clickedCell !== functionTarget.value && isFunctionActive.value) {
				const functionTable = functionTarget.value?.closest('table')
				const clickedTable = clickedCell.closest('table')
				if (functionTable && clickedTable && functionTable !== clickedTable) {
					finishFunction()
					document.removeEventListener('mousemove', tableMouseMoveEvent)
					document.removeEventListener('mouseup', tableMouseUpEvent)
					return
				}
				const targetFunction: any = document.getElementById(functionTarget.value.id)
				const position = cellPosition(clickedCell)
				const tempContent: any = targetFunction.textContent
				let tempContentLength = tempContent.length
				if (tempContent.includes(')')) {
					tempContentLength -= 1
				}
				const functionTempContent = tempContent.slice(tempContent.indexOf('(') + 1, tempContentLength)
				if (functionTempContent) {
					const isPosition = overFunctionList(functionTempContent, position)

					if (isPosition) return
				}
				const useSelection = new UseSelection()

				useSelection.saveRange(targetFunction.querySelector('div'), position, functionKeyType.value)

				const content = targetFunction.textContent
				let contentLength = content.length
				if (content.includes(')')) {
					contentLength -= 1
				}
				const functionContent = content.slice(content.indexOf('(') + 1, contentLength)
				functionActivePosition(functionContent)
			}
		} else if (isFunctionActive.value) {
			const position: any = cellMultiPosition()
			const useSelection = new UseSelection()
			useSelection.saveRange(functionTarget.value.querySelector('div'), position)
			functionActivePosition(position)
		}

		document.removeEventListener('mousemove', tableMouseMoveEvent)
		document.removeEventListener('mouseup', tableMouseUpEvent)
	}

	function selectorMouseDownEvent(event: any) {
		event.stopPropagation()
		event.preventDefault()

		const { cellTarget } = storeToRefs(useEditorStore())
		const target = event?.currentTarget || event?.target

		if (target && target.classList.contains('table--selector__pointer-left-top')) {
			selectorDragAnchor = findSelectionBoundaryCell('bottom-right')
		} else {
			selectorDragAnchor = findSelectionBoundaryCell('top-left')
		}

		if (!selectorDragAnchor) {
			selectorDragAnchor = cellTarget.value
		}

		document.addEventListener('mousemove', selectorMouseMoveEvent)
		document.addEventListener('mouseup', selectorMouseUpEvent)
	}

	function selectorMouseMoveEvent(event: any) {
		const { cellTarget } = storeToRefs(useEditorStore())
		const { target } = event
		const parent = target.closest('td')
		const table = target.closest('table')

		if (!parent || parent.classList.contains('number--line') || !table) return

		dragTableList = []

		const anchor = selectorDragAnchor || cellTarget.value
		dragTableList.push(anchor)

		if (!dragTableList.includes(parent)) {
			dragTableList.push(parent)
		}

		const useEditor = useEditorStore()
		const { dragList } = storeToRefs(useEditor)
		dragList.value = []
		dragList.value = dragTableList

		selectorOverlay = useSelectorOverlay()
		selectorOverlay.positionMultiSelector(table, dragTableList)
	}

	function selectorMouseUpEvent() {
		selectorDragAnchor = null
		document.removeEventListener('mousemove', selectorMouseMoveEvent)
		document.removeEventListener('mouseup', selectorMouseUpEvent)
	}

	function clearCell(event: any) {
		const { cellTarget } = storeToRefs(useEditorStore())

		event.preventDefault()
		event.stopPropagation()
		event.stopImmediatePropagation()
		const { target } = event
		const cell = target.closest('td')

		for (let key in cell.dataset) {
			delete cell.dataset[key]
		}

		cell.innerHTML = '<div contenteditable="true"></div>'

		const useCreate = useCreateTable()
		useCreate.event()

		cellTarget.value = cell
		directSocketSave('cell', cell)
	}

	async function tableOptionEvent(event: any) {
		const { target } = event
		const container = target.closest('.table--dropbox')
		const containerCell = target.closest('td')
		const block = target.closest('.editor--block__container')
		const containerRect = container.getBoundingClientRect()
		const containerCellRect = containerCell.getBoundingClientRect()

		const selectListContainer = block.querySelector('.table--selector__list')
		selectListContainer.style.display = 'block'
		selectListContainer.style.left = `${containerRect.left}px`
		selectListContainer.style.top = `${containerRect.top + containerCellRect.height - 20}px`
		selectListContainer.style.width = `${containerRect.width}px`

		const dropboxCode = containerCell.dataset.dropboxcode.split(',')
		const dropboxItemUid = containerCell.dataset.dropboxitemuid.split(',')
		const dropboxName = containerCell.dataset.dropboxname.split(',')
		let dropbox = ''
		dropbox += `<ul>`
		dropboxItemUid.forEach((itemUid: any, index: number) => {
			dropbox += `<li data-code="${dropboxCode[index]}"><p>${dropboxName[index]}</p></li>`
		})
		dropbox += `</ul>`

		block.querySelector('.table--selector__list').innerHTML = ''
		block.querySelector('.table--selector__list').innerHTML = dropbox

		await nextTick()

		const selectList = block.querySelectorAll('.table--selector__list li')
		selectList.forEach((item: any) => {
			item.addEventListener('click', (event: any) => {
				const { target } = event
				containerCell.dataset.dropboxSelect = target.closest('li').dataset.code
				const valueContainer = container.querySelector('.select--value p')
				valueContainer.innerHTML = target.innerHTML

				const selectCustom = container.querySelector('.select--custom')
				if (selectCustom && !container.querySelector('.ico--table__close')) {
					selectCustom.insertAdjacentHTML('afterend', '<i class="ico ico--table__close" data-testid="table-close-btn"></i>')
				}

				item.closest('.table--selector__list').style.display = 'none'

				const table = target.closest('.editor--block__container')
				table.style.zIndex = '1'

				directSocketSave('dropbox', containerCell)
			})
		})
	}

	function finishFunction() {
		const { isFunctionActive, functionTarget } = storeToRefs(useEditorStore())

		if (!isFunctionActive.value || !functionTarget.value) return

		let text = functionTarget.value.textContent || ''
		const openCount = (text.match(/\(/g) || []).length
		const closeCount = (text.match(/\)/g) || []).length

		if (openCount > closeCount) {
			const missingCount = openCount - closeCount
			functionTarget.value.querySelector('div').innerHTML += ')'.repeat(missingCount)
			text = functionTarget.value.textContent || ''
		}

		const firstOpenIndex = text.indexOf('(')
		const lastCloseIndex = text.lastIndexOf(')')
		const rawPosition = text.slice(firstOpenIndex + 1, lastCloseIndex)
		functionTarget.value.dataset.position = clampPositionString(rawPosition)
		functionTarget.value.dataset.formulas = 'false'
		const divElement = functionTarget.value.querySelector('div')
		divElement.setAttribute('contenteditable', 'false')
		divElement.blur()

		const selection = window.getSelection()
		if (selection) {
			selection.removeAllRanges()
		}

		calcFunction()
		isFunctionActive.value = false

		document.removeEventListener('mousemove', tableMouseMoveEvent)
		document.removeEventListener('mouseup', tableMouseUpEvent)

		const { isUndoRedoInProgress } = storeToRefs(useEditorStore())
		if (!isUndoRedoInProgress.value) {
			const history = new UseHistory()
			history.push()
		}
	}

	function focusOutEvent(event: any) {
		const { noteType, isFunctionActive, isUndoRedoInProgress } = storeToRefs(useEditorStore())

		if (isFunctionActive.value) return
		if (isUndoRedoInProgress.value) return

		const { target } = event
		const cell = target.closest('td')

		if (!cell || cell.classList.contains('number--line')) return

		if (typingHistoryManager) {
			typingHistoryManager.flush()
		}

		calcFunction()

		if (noteType.value === 'Project') {
			saveCellOnFocusOut(cell)
		}
	}

	function saveCellOnFocusOut(cell: HTMLElement) {
		const { socket } = storeToRefs(useCommonStore())
		const { noteUid } = storeToRefs(useEditorStore())

		const block = cell.closest('.editor--block')
		if (!block) return

		const table = block.querySelector('table')
		if (!table) return

		if (block.id.includes('temp') || table.id.includes('temp') || cell.id.includes('temp')) return

		const cellDiv = cell.querySelector('div')
		if (!cellDiv) return

		const blockUid = deletePrefix(block.id)
		const tableUid = deletePrefix(table.id)
		const dataType: any = setDataType(cell)

		const isFunction = cell.dataset.type === 'function'
		const isError = cell.dataset.type === 'error'
		const isImage = cell.dataset.type === 'image'
		const isPdf = cell.dataset.type === 'pdf'
		let cellContent: string
		if (isFunction || isError) {
			cellContent = cellDiv.querySelector('span')?.innerHTML ?? ''
		} else if (isImage) {
			cellContent = cellDiv.querySelector('img')?.outerHTML ?? ''
		} else if (isPdf) {
			cellContent = cell.dataset.pdfName || ''
		} else {
			cellContent = cellDiv.innerHTML
		}

		socket.value.updateCell(noteUid.value, blockUid, tableUid, deletePrefix(cell.id), {
			content: cellContent,
			dataType,
		})
	}

	return {
		keydownEvent,
		keyupEvent,
		inputEvent,
		pasteEvent,
		copyEvent,
		cutEvent,
		focusEvent,
		focusOutEvent,
		create,
		tableMouseDownEvent,
		selectorMouseDownEvent,
		clearCell,
		tableOptionEvent,
		finishFunction,
	}
}
